import type { ClientIntelligence, FlagTone } from '@/lib/clients/roster-intelligence';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Semantic status tokens (theme-aware) — not hardcoded palette scales.
const DOT_CLASSES: Record<FlagTone, string> = {
  danger: 'bg-status-deviation',
  warn: 'bg-status-warn',
  info: 'bg-status-info',
};

interface RosterStatusDotsProps {
  intelligence?: ClientIntelligence;
  className?: string;
}

/**
 * Compact attention signal for the roster: up to three colored dots beside the
 * client name. Hover shows the full flag labels (Lapsed, Reassess due, etc.).
 */
export function RosterStatusDots({ intelligence, className }: RosterStatusDotsProps) {
  if (!intelligence || intelligence.flags.length === 0) return null;

  const labels = intelligence.flags.map((flag) => flag.label).join(' · ');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center gap-0.5 shrink-0 rounded outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className
          )}
          data-testid="roster-status-dots"
          role="img"
          aria-label={labels}
          tabIndex={0}
        >
          {intelligence.flags.slice(0, 3).map((flag) => (
            <span
              key={flag.id}
              className={cn('h-2 w-2 rounded-full ring-1 ring-background', DOT_CLASSES[flag.tone])}
              aria-hidden
            />
          ))}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs">{labels}</p>
      </TooltipContent>
    </Tooltip>
  );
}
